import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useT, useLocale } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { searchOff } from "../lib/off";
import { searchVerified, verifiedToProduct } from "@shared/verifiedFoods";
import { defaultMealSlot, todayLocal } from "../lib/date";
import { FoodResultRow } from "../components/FoodResultRow";
import { LogMethodRow } from "../components/LogMethodRow";
import { VoiceLogSheet } from "../components/VoiceLogSheet";
import { SavedMealsSheet } from "../components/SavedMealsSheet";
import { QuickAddSheet } from "../components/QuickAddSheet";
import { premiumOnly, type LogMethod } from "../lib/logMethods";
import { useConfirm } from "../lib/confirm";
import { hapticSuccess } from "../lib/haptics";
import { QuantitySheet } from "../components/QuantitySheet";
import { FoodSheet } from "../components/FoodSheet";
import { saveFoodDraft } from "../lib/foodDraft";
import { ContinuousScanSheet } from "../components/ContinuousScanSheet";
import { usePhotoPicker } from "../lib/usePhotoPicker";
import { useEntitlement } from "../lib/useEntitlement";
import { Paywall } from "../components/Paywall";
import { PremiumFeatureIntro } from "../components/PremiumFeatureIntro";
import { detailAllowance } from "../lib/detailGate";
import { ListSkeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorState } from "../components/ui/ErrorState";
import { useCachedResource } from "../lib/useCachedResource";
import type { MealSlot, NormalizedProduct, FrequentFood, FavouriteFood, EstimateResponse } from "@shared/types";

interface Lists { frequent: FrequentFood[]; favourites: FavouriteFood[] }

// Your frequent foods change only when you log something, and logging
// something reloads them.
const FREQUENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const SLOTS: MealSlot[] = ["breakfast", "lunch", "dinner", "snack"];
function parseSlot(s: string | null): MealSlot {
  return (SLOTS as string[]).includes(s ?? "") ? (s as MealSlot) : defaultMealSlot(new Date().getHours());
}

type Sheet =
  | { kind: "none" }
  | { kind: "more" }
  | { kind: "photo"; file: File }
  | { kind: "voice"; estimate: EstimateResponse; transcript: string }
  | { kind: "barcode" }
  | { kind: "quantity"; product: NormalizedProduct };

export function AddFood() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const eatenOn = params.get("date") || todayLocal();
  const slot = parseSlot(params.get("slot"));

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NormalizedProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [sort, setSort] = useState<"smart" | "count" | "recent">("smart");
  // Your own foods, keyed by how they are sorted — smart order also depends on
  // the meal slot you arrived in. This list is the fastest path to logging
  // something, so it should be on screen before the request finishes; nothing
  // here is a number that misleads if it is a minute old.
  const lists = useCachedResource<Lists>(
    `frequent:${sort}${sort === "smart" ? `:${slot}` : ""}`,
    async () => {
      const [frequent, favourites] = await Promise.all([
        api.frequentFoods(sort, 20, sort === "smart" ? slot : undefined),
        // Stars are decorative next to the list itself — losing them must not
        // cost you the foods.
        api.favourites().catch(() => [] as FavouriteFood[]),
      ]);
      return { frequent, favourites };
    },
    { maxAgeMs: FREQUENT_MAX_AGE_MS },
  );
  const frequent = lists.data?.frequent ?? null;
  const favs = lists.data?.favourites ?? [];
  const loadFrequent = lists.reload;
  const [sheet, setSheet] = useState<Sheet>({ kind: "none" });
  const [showVoice, setShowVoice] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showQuick, setShowQuick] = useState(false);
  const confirm = useConfirm();
  // Photo and barcode are Premium logging methods (same gate as Today's
  // tiles); text search on this screen stays free.
  const { premium } = useEntitlement();
  const [showPaywall, setShowPaywall] = useState(false);
  const [scanIntro, setScanIntro] = useState(false);
  const { openPicker, photoInput } = usePhotoPicker((file) => setSheet({ kind: "photo", file }));
  const searchRef = useRef<HTMLInputElement | null>(null);

  async function toggleStar(name: string, next: boolean) {
    // Optimistic: flip locally, reconcile on next load.
    lists.setData((cur) => cur && {
      frequent: cur.frequent.map((f) => (f.name === name ? { ...f, starred: next } : f)),
      favourites: next ? cur.favourites : cur.favourites.filter((f) => f.name !== name),
    });
    try { await api.setFavourite(name, next); } finally { await loadFrequent(); }
  }

  // Debounced, abortable search. Aborted requests are ignored (no error flash).
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); setSearchErr(false); return; }
    const ctrl = new AbortController();
    setSearching(true); setSearchErr(false);
    const id = setTimeout(async () => {
      try {
        const r = await searchOff(q, ctrl.signal);
        if (!ctrl.signal.aborted) { setResults(r); setSearching(false); }
      } catch {
        if (ctrl.signal.aborted) return;
        setSearchErr(true); setSearching(false);
      }
    }, 350);
    return () => { clearTimeout(id); ctrl.abort(); };
  }, [query, nonce]);

  async function quickAdd(f: FrequentFood) {
    try {
      await api.addFood({
        eaten_on: eatenOn, source: "custom", plan_meal_slot: slot,
        name: f.name, calories: f.calories,
        // Detail amounts are Pro — a free quick-log is the bare entry.
        ...detailAllowance(premium, { protein_g: f.protein_g, carbs_g: f.carbs_g, fat_g: f.fat_g }),
      });
      toast.success(t("toast.logged"));
      loadFrequent();
    } catch {
      toast.error(t("toast.failed"));
    }
  }

  // F9: the verified layer resolves locally (instant, offline-capable) and is
  // shown above the crowdsourced OpenFoodFacts results.
  const verified = searchVerified(query.trim()).map((f) => verifiedToProduct(f, locale));
  const showSearch = query.trim().length >= 2;
  // This screen is THE logging hub (owner, 2026-08-09): every method from
  // the old Today grid, with this screen's slot as the destination — the +
  // you tapped on a meal already chose the timing.
  function pickLogMethod(method: LogMethod) {
    // Premium method on a free account: camera gets the visual "watch it
    // work" first (paywall after Unlock); voice goes straight to the paywall.
    if (premiumOnly(method) && !premium) {
      if (method === "photo") setScanIntro(true);
      else setShowPaywall(true);
      return;
    }
    if (method === "photo") openPicker(); // native picker first — sheet opens after a photo is chosen
    else if (method === "barcode") setSheet({ kind: "barcode" });
    else if (method === "describe") setSheet({ kind: "more" });
    else if (method === "voice") setShowVoice(true);
    else if (method === "saved") setShowSaved(true);
    else if (method === "quick") setShowQuick(true);
    else if (method === "copy_yesterday") void copySlotFromYesterday();
  }

  async function copySlotFromYesterday() {
    const from = new Date(Date.parse(eatenOn + "T12:00:00Z") - 86400_000).toISOString().slice(0, 10);
    const ok = await confirm({ message: t("copy.confirm"), confirmLabel: t("log.copy_yesterday") });
    if (!ok) return;
    const r = await api.copyDay(from, eatenOn, slot);
    if (r.copied === 0) toast.error(t("copy.nothing_yesterday"));
    else { hapticSuccess(); toast.success(t("copy.done")); await loadFrequent(); }
  }

  return (
    <div className="min-h-screen-d bg-canvas text-fg flex flex-col">
      <header className="flex items-center justify-between px-5 pt-safe pt-4 pb-2">
        <button onClick={() => navigate(-1)} aria-label={t("common.back")} className="text-fg-muted text-2xl leading-none -ml-1 press">‹</button>
        <h1 className="text-base font-semibold truncate px-2">{t("add.title")} · {t(`meal.header.${slot}`)}</h1>
        <button onClick={() => navigate(-1)} className="text-brand text-sm font-semibold press">{t("add.done")}</button>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pb-24 space-y-4">
        <LogMethodRow onPick={pickLogMethod} />

        <input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("add.search_placeholder", { meal: t(`meal.header.${slot}`) })}
          className="input"
          inputMode="search"
        />

        {showSearch ? (
          <div>
            {searching && <ListSkeleton rows={5} />}
            {!searching && searchErr && (
              <ErrorState message={t("food_sheet.ai_failed")} onRetry={() => setNonce((n) => n + 1)} />
            )}
            {verified.length > 0 && (
              <div className="divide-y divide-line mb-3">
                {verified.map((p) => (
                  <FoodResultRow
                    key={p.code}
                    title={p.name}
                    value={`${p.per_100g.kcal} kcal/100${p.unit}`}
                    verified
                    onAdd={() => setSheet({ kind: "quantity", product: p as NormalizedProduct })}
                  />
                ))}
              </div>
            )}
            {!searching && !searchErr && results.length === 0 && verified.length === 0 && (
              <EmptyState message={t("add.no_results")} />
            )}
            {!searching && !searchErr && results.length > 0 && (
              <div className="divide-y divide-line">
                {results.map((p) => (
                  <FoodResultRow
                    key={p.code || p.name}
                    title={p.name}
                    subtitle={p.brand || undefined}
                    value={`${p.per_100g.kcal} kcal/100${p.unit}`}
                    onAdd={() => setSheet({ kind: "quantity", product: p })}
                  />
                ))}
              </div>
            )}
            {!searching && (verified.length > 0 || results.length > 0) && (
              <p className="text-sm text-fg-faint mt-3 leading-relaxed">{t("food.sources_note")}</p>
            )}
          </div>
        ) : (
          <div>
            {favs.length > 0 && (
              <div className="mb-4">
                <div className="text-sm text-fg-muted uppercase tracking-wider mb-1">{t("add.favourites")}</div>
                <div className="divide-y divide-line">
                  {favs.filter((f) => f.calories !== null).map((f) => (
                    <FoodResultRow
                      key={f.name}
                      title={f.name}
                      value={`${f.calories} kcal`}
                      starred
                      onToggleStar={(next) => toggleStar(f.name, next)}
                      onAdd={() => quickAdd({ ...f, calories: f.calories! } as FrequentFood)}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 mb-2">
              <SortChip active={sort === "smart"} label={t("add.smart")} onClick={() => setSort("smart")} />
              <SortChip active={sort === "count"} label={t("add.frequent")} onClick={() => setSort("count")} />
              <SortChip active={sort === "recent"} label={t("add.recent")} onClick={() => setSort("recent")} />
            </div>
            {/* A failed load used to fall back to an empty array, so "we could
                not reach the server" rendered as "you have never logged
                anything" — the one failure users cannot tell from the truth. */}
            {lists.error && frequent === null ? (
              <ErrorState message={t("error.load_failed")} onRetry={loadFrequent} />
            ) : frequent === null ? (
              <ListSkeleton rows={5} />
            ) : frequent.length === 0 ? (
              <EmptyState message={t("add.frequent_empty")} />
            ) : (
              <div className="divide-y divide-line">
                {frequent.map((f) => (
                  <FoodResultRow
                    key={f.name}
                    title={f.name}
                    value={`${f.calories} kcal`}
                    starred={f.starred}
                    onToggleStar={(next) => toggleStar(f.name, next)}
                    onAdd={() => quickAdd(f)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {sheet.kind === "quantity" && (
        <QuantitySheet
          product={sheet.product} slot={slot} eatenOn={eatenOn}
          onAdded={loadFrequent} onClose={() => setSheet({ kind: "none" })}
        />
      )}
      {(sheet.kind === "more" || sheet.kind === "photo" || sheet.kind === "voice") && (
        <FoodSheet
          open mode="add" initial={{}} initialPhoto={sheet.kind === "photo" ? sheet.file : null}
          initialEstimate={sheet.kind === "voice" ? { response: sheet.estimate, transcript: sheet.transcript } : undefined}
          context={{ eaten_on: eatenOn, source: "custom", plan_meal_slot: slot }}
          onSaved={async (draft) => { await saveFoodDraft(draft); await loadFrequent(); }}
          onClose={() => setSheet({ kind: "none" })}
        />
      )}
      {photoInput}
      {scanIntro && (
        <PremiumFeatureIntro
          onUnlock={() => { setScanIntro(false); setShowPaywall(true); }}
          onClose={() => setScanIntro(false)}
        />
      )}
      {showPaywall && <Paywall trigger="feature" onClose={() => setShowPaywall(false)} />}
      {sheet.kind === "barcode" && (
        <ContinuousScanSheet
          eatenOn={eatenOn} defaultSlot={slot}
          onDone={async () => { setSheet({ kind: "none" }); await loadFrequent(); }}
          onClose={() => setSheet({ kind: "none" })}
        />
      )}
      {showVoice && (
        <VoiceLogSheet
          onResult={(estimate, transcript) => { setShowVoice(false); setSheet({ kind: "voice", estimate, transcript }); }}
          onClose={() => setShowVoice(false)}
        />
      )}
      {showSaved && <SavedMealsSheet date={eatenOn} onLogged={loadFrequent} onClose={() => setShowSaved(false)} />}
      {showQuick && <QuickAddSheet date={eatenOn} initialSlot={slot} onDone={loadFrequent} onClose={() => setShowQuick(false)} />}
    </div>
  );
}

function SortChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm font-medium press ${active ? "bg-brand text-on-brand" : "bg-surface text-fg-muted"}`}
    >
      {label}
    </button>
  );
}
